import React, { createRef, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Thumbs, FreeMode, Zoom } from 'swiper';
import 'swiper/css/bundle';
import 'swiper/css/zoom';
import Icon from '../Icon';
import SliderImage from '../../Types/SliderImage';

interface FullscreenImageSliderProps {
  showCloseButton?: boolean;
  images: SliderImage[];
  initialSlide?: number;
  onClosed?: () => void;
}

const FullscreenImageSlider = ({
  showCloseButton = true,
  images,
  initialSlide = 0,
  onClosed,
}: FullscreenImageSliderProps) => {
  const dispatch = useDispatch();
  const contentRef = createRef<HTMLDivElement>();

  const [open, setOpen] = useState(true);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [zoomInfoVisible, setZoomInfoVisible] = useState<boolean>(true);
  const [hasLoadedOriginalImage, setHasLoadedOriginalImage] = useState<
    number[]
  >([]);
  const imageCount = useMemo(() => images.length, [images]);

  const onKeyDown = (e) => {
    if (e.key == 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    dispatch(
      addOverlay('FullscreenImageSlider', () => {
        setOpen(false);
      })
    );
    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      dispatch(removeOverlay('FullscreenImageSlider'));
      onClosed && onClosed();
    }
  }, [dispatch, open, onClosed]);

  useEffect(() => {
    setTimeout(() => setZoomInfoVisible(false), 5000);
  }, []);

  const onZoom = (slideIndex: number, scale: number) => {
    setIsZoomed(scale > 1);
    if (!hasLoadedOriginalImage.includes(slideIndex)) {
      setHasLoadedOriginalImage((hasLoadedOriginalImage) => [
        ...hasLoadedOriginalImage,
        slideIndex,
      ]);
    }
  };

  return open ? (
    <div className="fixed inset-0 z-[55] h-screen w-full bg-white">
      <div className="h-full" ref={contentRef}>
        {showCloseButton && (
          <Icon
            className="absolute right-4 top-4 z-[55] h-5 w-5 cursor-pointer"
            name="close"
            onClick={() => setOpen(false)}
          />
        )}

        <Swiper
          className="h-full [&_.swiper-pagination]:!bottom-8 [&_.swiper-pagination]:!block"
          loop={true}
          slidesPerView={1}
          initialSlide={initialSlide}
          zoom={{
            toggle: true,
          }}
          modules={[Pagination, Thumbs, FreeMode, Zoom]}
          onZoomChange={(swiper, scale) => onZoom(swiper.realIndex, scale)}
          pagination={{
            dynamicBullets: true,
            dynamicMainBullets: 3,
          }}
        >
          {images?.map((img, idx) => (
            <SwiperSlide
              key={'fullwidth-image-slide' + idx + img.hdSrc}
              zoom={true}
            >
              <img
                className="h-full"
                src={
                  isZoomed || hasLoadedOriginalImage.includes(idx)
                    ? img.originalSrc
                    : img.hdSrc
                }
                loading={idx == 0 ? 'eager' : 'lazy'}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  ) : null;
};

export default FullscreenImageSlider;
